import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchModulePermissions,
  fetchRolesList,
  saveModulePermissions,
} from "../../api/userBuilderApi";
import { isProtectedAdminUserManagementRow, isProtectedPortalRoleUser } from "../../../../public/utils/roleAccess";
import PermissionMatrixTable from "../permissions/PermissionMatrixTable";
import PermissionsWorkspace from "../permissions/PermissionsWorkspace";

export default function RoleModulePermissionsSection({ onNotify }) {
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadRoles = useCallback(async () => {
    try {
      const r = await fetchRolesList(1, 200);
      setRoleOptions(
        (r.rows || [])
          .filter((row) => !isProtectedPortalRoleUser(row))
          .map((row) => ({
            value: String(row.id),
            label: row.role_name,
          })),
      );
    } catch (e) {
      onNotify?.(e.message || "Could not load roles.", { variant: "error" });
    }
  }, [onNotify]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const loadMatrix = useCallback(async () => {
    const rid = Number(selectedRoleId);
    if (!rid) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchModulePermissions(rid);
      setRows(
        (data || []).map((row) => ({
          ...row,
          _rowKey: `m-${row.module_id}`,
          _label: row.module_name,
          _sublabel: row.module_slug,
        })),
      );
    } catch (e) {
      setRows([]);
      setLoadError(e.message || "Could not load module permissions.");
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const tableRows = useMemo(() => rows, [rows]);
  const roleId = Number(selectedRoleId);

  const isRowDisabled = useCallback(
    (row) => isProtectedAdminUserManagementRow(roleId, row),
    [roleId],
  );

  const onToggle = (rowKey, permKey, checked) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._rowKey !== rowKey) return row;
        if (isRowDisabled(row)) return row;
        return { ...row, [permKey]: checked ? 1 : 0 };
      }),
    );
  };

  const onPatchRow = (rowKey, patch) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._rowKey !== rowKey) return row;
        if (isRowDisabled(row)) return row;
        return { ...row, ...patch };
      }),
    );
  };

  const onPatchColumn = (permKey, turnOn, rowDisabled) => {
    setRows((prev) =>
      prev.map((row) => {
        if (rowDisabled?.(row)) return row;
        return { ...row, [permKey]: turnOn ? 1 : 0 };
      }),
    );
  };

  const onGrantAllView = () => {
    setRows((prev) =>
      prev.map((row) => (isRowDisabled(row) ? row : { ...row, can_view: 1 })),
    );
  };

  const onClearAll = () => {
    setRows((prev) =>
      prev.map((row) => {
        if (isRowDisabled(row)) return row;
        return {
          ...row,
          can_view: 0,
          can_create: 0,
          can_edit: 0,
          can_delete: 0,
        };
      }),
    );
  };

  const onSave = async () => {
    const rid = Number(selectedRoleId);
    if (!rid) {
      onNotify?.("Select a role first.", { variant: "error" });
      return;
    }
    setSaving(true);
    onNotify?.("");
    try {
      const payload = rows.map((row) => ({
        module_id: row.module_id,
        can_view: row.can_view,
        can_create: row.can_create,
        can_edit: row.can_edit,
        can_delete: row.can_delete,
      }));
      await saveModulePermissions(rid, payload);
      onNotify?.("Module permissions saved.");
      window.dispatchEvent(new CustomEvent("ub-module-permissions-saved"));
      await loadMatrix();
    } catch (e) {
      onNotify?.(e.message || "Could not save module permissions.", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionsWorkspace
      id="ub-module-permissions"
      variant="module"
      title="Menu modules"
      lead="Grant top-level module access. User management for Admin is fixed by the system and cannot be changed here."
      roleOptions={roleOptions}
      selectedRoleId={selectedRoleId}
      onRoleChange={setSelectedRoleId}
      rows={tableRows}
      loading={loading}
      saving={saving}
      loadError={loadError}
      onSave={onSave}
      onGrantAllView={onGrantAllView}
      onClearAll={onClearAll}
    >
      {(filteredRows) => (
        <PermissionMatrixTable
          rows={filteredRows}
          nameColumn="Module"
          onToggle={onToggle}
          onPatchRow={onPatchRow}
          onPatchColumn={onPatchColumn}
          disabled={!selectedRoleId || saving}
          isRowDisabled={isRowDisabled}
        />
      )}
    </PermissionsWorkspace>
  );
}
